"use client";

import { useState, useEffect } from "react";
import { Button } from "../button";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import {
  Menu,
  X,
  LogOut,
  LayoutDashboard,
  ChevronRight,
  Home,
  Briefcase,
  Users,
  FileText,
  BookOpen,
  User,
} from "lucide-react";
import { ModeToggle } from "../../mode-toggle";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../../api/client";
import { Logo } from "../../Logo";

export default function Navbar({ onToggleSidebar }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [profile, setProfile] = useState(null);
  const [userName, setUserName] = useState("");

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Fetch user details and profile
  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!user) return;

      try {
        // For APPLIER: Fetch profile for full name
        if (user.role?.toUpperCase() === "APPLIER") {
          try {
            const res = await api.get("/applier/profile");
            if (res.data.fullName) {
              setProfile(res.data);
              setUserName(res.data.fullName);
            } else {
              setUserName(user.email?.split("@")[0] || "User");
            }
          } catch (err) {
            console.error("Failed to fetch profile:", err);
            setUserName(user.email?.split("@")[0] || "User");
          }
        }
        // For RECRUITER: Fetch recruiter profile
        else if (user.role?.toUpperCase() === "RECRUITER") {
          try {
            const res = await api.get("/recruiter/profile");
            if (res.data.companyName) {
              setUserName(res.data.companyName);
            } else {
              setUserName(user.email?.split("@")[0] || "Recruiter");
            }
          } catch (err) {
            console.error("Failed to fetch recruiter profile:", err);
            setUserName(user.email?.split("@")[0] || "Recruiter");
          }
        }
        // For ADMIN or others
        else {
          if (user.name || user.firstName) {
            setUserName(user.name || user.firstName);
          } else {
            setUserName(
              user.email?.split("@")[0] ||
                (user.role?.toUpperCase() === "ADMIN" ? "Admin" : "User")
            );
          }
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
        setUserName(user?.email?.split("@")[0] || "User");
      }
    };

    fetchUserDetails();
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate("/login");
    setMobileOpen(false);
  };

  // Navigation links based on user role
  const getNavLinks = () => {
    if (!user) return [];

    const baseLinks = [
      { label: "Home", to: "/", icon: <Home className="w-4 h-4" /> },
      { label: "Blog", to: "/blog", icon: <BookOpen className="w-4 h-4" /> },
    ];

    switch (user.role?.toUpperCase()) {
      case "APPLIER":
        return [
          ...baseLinks,
          {
            label: "Jobs",
            to: "/jobs",
            icon: <Briefcase className="w-4 h-4" />,
          },
          {
            label: "My Application",
            to: "/applier/applications",
            icon: <FileText className="w-4 h-4" />,
          },
          {
            label: "Dashboard",
            to: `/${user.role?.toLowerCase()}/dashboard`,
            icon: <LayoutDashboard className="w-4 h-4" />,
          },
        ];
      case "RECRUITER":
        return [
          ...baseLinks,
          {
            label: "Post Job",
            to: "/recruiter/post-job",
            icon: <Briefcase className="w-4 h-4" />,
          },
          {
            label: "Applications",
            to: "/recruiter/applications",
            icon: <Users className="w-4 h-4" />,
          },
          {
            label: "Dashboard",
            to: "/recruiter/dashboard",
            icon: <LayoutDashboard className="w-4 h-4" />,
          },
        ];
      case "ADMIN":
        return [
          ...baseLinks,
          {
            label: "Users",
            to: "/admin/users",
            icon: <Users className="w-4 h-4" />,
          },
          {
            label: "Create Admin",
            to: "/admin/create-admin",
            icon: <Briefcase className="w-4 h-4" />,
          },
          {
            label: "Dashboard",
            to: "/admin/dashboard",
            icon: <LayoutDashboard className="w-4 h-4" />,
          },
        ];
      default:
        return baseLinks;
    }
  };

  const navLinks = getNavLinks();

  // Determine display name and role
  const displayName =
    userName ||
    user?.email?.split("@")[0] ||
    (user?.role?.toUpperCase() === "ADMIN" ? "Admin" : "User");

  const displayRole = user?.role
    ? user.role.toUpperCase() === "APPLIER"
      ? "Applier"
      : user.role.toUpperCase() === "RECRUITER"
        ? "Recruiter"
        : user.role.toUpperCase() === "ADMIN"
          ? "Administrator"
          : user.role.charAt(0).toUpperCase() + user.role.slice(1).toLowerCase()
    : "";

  // Get initials for avatar
  const getInitials = () => {
    if (displayName && displayName.length > 0) {
      return displayName.charAt(0).toUpperCase();
    }
    if (user?.email && user.email.length > 0) {
      return user.email.charAt(0).toUpperCase();
    }
    return "U";
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 border-b ${
        scrolled
          ? "bg-card/80 backdrop-blur-xl shadow-sm"
          : "bg-card/60 backdrop-blur-md"
      }`}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* LEFT SIDE: Logo */}
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="relative h-10 w-auto min-w-[120px] transition-all duration-300">
                <Logo className="w-full h-full object-contain" />
              </div>
            </Link>
          </div>

          {/* DESKTOP NAVIGATION */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 relative ${
                  location.pathname === link.to
                    ? "text-primary bg-primary/5"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent"
                }`}
              >
                {link.icon}
                {link.label}
                {location.pathname === link.to && (
                  <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary rounded-full" />
                )}
              </Link>
            ))}

            <div className="h-6 w-px bg-border mx-2" />

            {/* User Actions */}
            <div className="flex items-center gap-2">
              <ModeToggle />

              {user ? (
                <div className="flex items-center gap-2">
                  {/* User Profile - Clickable */}
                  <button
                    onClick={() => {
                      const role = user.role?.toUpperCase();
                      if (role === "APPLIER") navigate("/profile");
                      else if (role === "RECRUITER")
                        navigate("/recruiter/profile");
                      else navigate("/admin/profile");
                    }}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-card hover:bg-accent transition-all duration-200"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold text-sm">
                      {getInitials()}
                    </div>
                    <div className="hidden xl:block text-left">
                      <p className="text-sm font-medium">{displayName}</p>
                      <p className="text-xs text-muted-foreground capitalize">
                        {displayRole}
                      </p>
                    </div>
                  </button>

                  {/* Logout Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLogout}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    <span className="hidden xl:inline">Logout</span>
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Button asChild variant="ghost" size="sm">
                    <Link to="/login">Login</Link>
                  </Button>
                  <Button asChild size="sm">
                    <Link to="/register">
                      Get Started
                      <ChevronRight className="ml-1 w-4 h-4" />
                    </Link>
                  </Button>
                </div>
              )}
            </div>
          </nav>

          {/* MOBILE RIGHT SIDE */}
          <div className="flex lg:hidden items-center gap-3">
            <ModeToggle />

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="p-2 rounded-lg hover:bg-accent transition-all duration-200"
            >
              <AnimatePresence mode="wait">
                {mobileOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X className="w-6 h-6" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu className="w-6 h-6" />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </div>
        </div>
      </div>

      {/* MOBILE MENU DROPDOWN */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="lg:hidden border-t bg-card backdrop-blur-xl overflow-hidden"
          >
            <div className="px-4 py-6 space-y-4">
              {/* User Profile Section */}
              {user && (
                <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50 border">
                  <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold text-lg">
                    {getInitials()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{displayName}</p>
                    <p className="text-sm text-muted-foreground truncate">
                      {user.email}
                    </p>
                    <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full bg-primary/10 text-primary">
                      {displayRole}
                    </span>
                  </div>
                </div>
              )}

              {/* Navigation Links */}
              <div className="space-y-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-200 ${
                      location.pathname === link.to
                        ? "bg-primary/5 text-primary"
                        : "hover:bg-accent"
                    }`}
                  >
                    <div
                      className={`p-2 rounded-lg ${
                        location.pathname === link.to
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted"
                      }`}
                    >
                      {link.icon}
                    </div>
                    <span className="font-medium flex-1">{link.label}</span>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </Link>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="pt-4 border-t">
                {user ? (
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setMobileOpen(false);
                        navigate(
                          user.role?.toUpperCase() === "APPLIER"
                            ? "/profile"
                            : user.role?.toUpperCase() === "RECRUITER"
                              ? "/recruiter/profile"
                              : "/admin/profile"
                        );
                      }}
                    >
                      <User className="w-4 h-4 mr-2" />
                      Profile
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleLogout}
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setMobileOpen(false)}
                      asChild
                    >
                      <Link to="/login">Login</Link>
                    </Button>
                    <Button onClick={() => setMobileOpen(false)} asChild>
                      <Link to="/register">Sign Up</Link>
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
